import { useEffect, useMemo, useState } from "react";
import api, { formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusPill from "@/components/StatusPill";
import { Search } from "lucide-react";

const FILTERS = [
  { k: "all",     l: "All" },
  { k: "active",  l: "Active" },
  { k: "inactive",l: "Inactive" },
  { k: "kyc",     l: "KYC Pending" },
  { k: "left",    l: "Left leg" },
  { k: "right",   l: "Right leg" },
];

const SORTS = [
  { k: "newest",   l: "Newest first" },
  { k: "oldest",   l: "Oldest first" },
  { k: "sp",       l: "By SP" },
  { k: "package",  l: "By package" },
  { k: "name",     l: "By name" },
];

export default function DistDownline() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => { api.get("/dist/downline").then((r) => setRows(r.data)); }, []);

  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    let out = rows.filter((u) => {
      if (filter === "active"   && !u.active) return false;
      if (filter === "inactive" && u.active) return false;
      if (filter === "kyc"      && u.kyc_status === "approved") return false;
      if (filter === "left"     && u.placement !== "L") return false;
      if (filter === "right"    && u.placement !== "R") return false;
      if (!qq) return true;
      return (
        (u.name || "").toLowerCase().includes(qq) ||
        (u.email || "").toLowerCase().includes(qq) ||
        (u.user_code || "").toLowerCase().includes(qq)
      );
    });
    const cmp = {
      newest:  (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest:  (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      sp:      (a, b) => (b.total_sp || 0) - (a.total_sp || 0),
      package: (a, b) => (b.package_sp || 0) - (a.package_sp || 0),
      name:    (a, b) => (a.name || "").localeCompare(b.name || ""),
    }[sort];
    return [...out].sort(cmp);
  }, [rows, q, filter, sort]);

  return (
    <DashboardLayout
      title="My Downline"
      subtitle={`${visible.length} of ${rows.length} direct referral${rows.length === 1 ? "" : "s"}`}
    >
      {/* Filters */}
      <div className="bg-white border border-border p-4 rounded-sm mb-4 flex flex-col md:flex-row gap-3" data-testid="downline-filters">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="downline-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, code…"
            className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-gold outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFilter(f.k)}
              data-testid={`downline-filter-${f.k}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border rounded-sm transition-colors ${
                filter === f.k ? "bg-navy text-gold border-navy" : "border-border text-navy hover:border-gold"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="downline-sort"
          className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold outline-none"
        >
          {SORTS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
        </select>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-navy text-white">
            <tr>
              {["User Code", "Name", "Email", "Role", "Package", "Side", "SP", "KYC", "Joined"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-navy/50">
                {rows.length === 0 ? "No direct referrals yet" : "No members match these filters"}
              </td></tr>
            )}
            {visible.map((u) => (
              <tr key={u.user_code} data-testid={`downline-${u.user_code}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                <td className="px-4 py-3 font-medium text-navy font-mono">{u.user_code}</td>
                <td className="px-4 py-3 text-navy/70">{u.name}</td>
                <td className="px-4 py-3 text-navy/60">{u.email}</td>
                <td className="px-4 py-3 capitalize text-navy/60">{u.role === "distributor" ? "IBA" : u.role}</td>
                <td className="px-4 py-3 text-gold-deep">{u.package_code || "—"}</td>
                <td className="px-4 py-3">
                  {u.placement === "L" ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-olive/10 text-olive border border-olive/25">Left</span>
                  ) : u.placement === "R" ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose/10 text-rose border border-rose/25">Right</span>
                  ) : <span className="text-navy/40">—</span>}
                </td>
                <td className="px-4 py-3 text-olive font-medium">{u.total_sp || 0}</td>
                <td className="px-4 py-3"><StatusPill status={u.kyc_status || "not_submitted"} size="sm" /></td>
                <td className="px-4 py-3 text-navy/50 text-xs">{formatDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
